// =============================================================================
// ui/tabs/CoachProLock.tsx — the two SECTION locks on the Coach's Report.
// -----------------------------------------------------------------------------
// ── 🚨 WHY A SECTION LOCK AND NOT ANOTHER ACTION GATE ──
//
// `useProAction` refuses a CLICK: the reader presses a button, the button
// explains itself, and everything around it stays on screen. That is right for
// "Coach Me" on a free tab, and it is wrong for a whole act of the methodology.
//
// The old client draws the line the same way. Its leak loop reads `isProUser()`
// once and swaps the FIX and PROVE cards for a single lock
// (`renderLeakLoopProLock`, app/index.html:43337, chosen at :43372); the four
// dimensions get the same treatment inline (:42266). A free user never reaches a
// Prove-it control to press, so there is no click to refuse — which is exactly
// why `domain/proAction.ts` does NOT carry a 'prove-it' entry. This file is the
// mechanism that its header promised and, until now, nothing provided.
//
// ── 🚨 WHAT A MISSING LOCK ACTUALLY LEAKS ──
//
// Not the Fix button — that has been gated since #76's first half. It is
// everything with no button in front of it: the running rules, each verdict,
// Bank and Retire, and the banked-habits ledger. The paid half of "Spot it. Fix
// it. Prove it." is mostly READING, and a gate built only from click guards
// leaves all of it visible. Same shape as the AI verdict card firing from an
// effect — find the choke point, not the controls.
//
// ── ⚠ THE COPY IS THE OLD CLIENT'S, VERBATIM ──
//
// This is the product's sales pitch and not copy to improve during a port.
//
// ✅ The one cut used to be beta's "and period-over-period comparison", because
// that strip was not ported and a lock card must never sell a screen the app
// cannot show once the reader pays. The strip has since landed — see
// `CoachTrendStrip` in `CoachReport.tsx` — so the promise is restored along with
// the thing it promises. The rule stands even though this instance of it no
// longer applies: RE-CHECK THIS LINE against what the app actually renders
// before the next edit to it.
//
// ⚠ NO NEW CSS — `.card`, `.muted`, `.btn primary` and the `.coach-step-*`
// classes all have rules, and `scripts/check-styles.mjs` fails the build for a
// class that does not. The handful of layout values are inline for the same
// reason `ProUpgrade` states: they carry no information a rule would.
//
// ⚠ THE TWO HEADINGS COME FROM THE CATALOGUE, not from here. `PRO_SECTION_TITLE`
// is what makes the section list load-bearing — a catalogue no screen ever names
// is indistinguishable from a section nobody locked, which is the defect this
// whole area keeps producing (#55, then #76, then #90).
// =============================================================================

import { PRO_SECTION_TITLE } from '../../selectors/proAction';

/**
 * One lock card. Shared shape, because the old client's two are identical
 * except for their words — 🔒, a title, one paragraph, and a way to pay.
 */
function LockCard(
  { testId, title, blurb, onSeePlans }: {
    testId: string;
    title: string;
    blurb: string;
    onSeePlans?: (() => void) | undefined;
  },
) {
  return (
    <div
      className="card"
      data-testid={testId}
      style={{ textAlign: 'center', padding: '22px 18px' }}
    >
      <div style={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, margin: '8px 0 4px' }}>{title}</div>
      <p
        className="muted"
        style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 470, margin: '0 auto' }}
      >
        {blurb}
      </p>
      {/* ⚠ ALWAYS A NEXT STEP. A lock with no route to the plan is a dead end
          that converts nobody — the old client wires both of its CTAs to the
          account tab and this does the same. */}
      {onSeePlans ? (
        <button
          type="button"
          className="btn primary"
          style={{ marginTop: 14 }}
          onClick={onSeePlans}
        >
          See plans
        </button>
      ) : null}
    </div>
  );
}

/**
 * In place of Fix it, Prove it and the banked-habits ledger.
 *
 * ⚠ SPOT IT STAYS FULLY READABLE ABOVE THIS, and that is the product, not a
 * concession: a trader must be able to see what their habits cost before they
 * have any reason to pay for the half that closes them.
 */
export function CoachProLock({ onSeePlans }: { onSeePlans?: (() => void) | undefined }) {
  return (
    <LockCard
      testId="coach-pro-lock"
      title={PRO_SECTION_TITLE['fix-prove']}
      blurb={
        'You can see exactly what’s costing you above. Pro turns your leaks into '
        + 'testable rules — one per leak, each with a frozen baseline — and proves '
        + 'whether each actually moved the number. The whole Spot → Fix → Prove '
        + 'loop, plus your banked-habits ledger.'
      }
      onSeePlans={onSeePlans}
    />
  );
}

/**
 * In place of the four lenses.
 *
 * ⚠ THE HEADING STAYS. The old client keeps its "The four dimensions" section
 * label above the lock, and the reason is conversion: a reader who cannot see
 * that a section exists cannot want it. The panels are what swap, not the
 * section.
 */
export function CoachDimensionsLock(
  { onSeePlans }: { onSeePlans?: (() => void) | undefined },
) {
  return (
    <section className="card" data-testid="coach-dimensions-locked">
      <div className="coach-step-head">
        <div>
          <div className="coach-step-t">Four more lenses</div>
          <p className="muted">
            Reads that aren’t single fixable leaks — whether the edge is there at
            all, and where it lives.
          </p>
        </div>
      </div>
      <LockCard
        testId="coach-dimensions-lock"
        title={PRO_SECTION_TITLE['four-lenses']}
        blurb={
          'Risk of ruin, Capture & edge, the confluences carrying your edge, and '
          + 'Pattern × exit — the four lenses beyond your leaks — plus the AI '
          + 'Coach verdict and period-over-period comparison.'
        }
        onSeePlans={onSeePlans}
      />
    </section>
  );
}

/**
 * What the paid half shows while the subscription read is still in flight.
 *
 * 🚨 NOT A SPINNER FOR ITS OWN SAKE, and the reasoning is the same one
 * `ProGateChecking` carries for a whole tab: access fails OPEN on an unknown
 * read, so without this the paid sections render in full for the frame before
 * the answer lands, then vanish. A free user would see the thing they cannot
 * have flash past on every visit.
 *
 * ⚠ Unreachable while `PAID_LAUNCH` is off — `pending` is hard-wired false.
 */
export function CoachSectionChecking() {
  return (
    <div className="card" data-testid="coach-pro-checking" style={{ textAlign: 'center' }}>
      <p className="muted">Checking your subscription…</p>
    </div>
  );
}
